/**
 * Design tokens for LeadDesk AI Insights based on the Luisa design system.
 * https://confluence.leaddesk.ch/display/RND/LeadDesk+Design+System
 */

import luisa from './luisa';

export const colors = {
    primary: {
        main: luisa.swatch('leadblue', '100'),
        dark: luisa.swatch('devblue', '95'),
        light: luisa.swatch('devblue', '30'),
        contrastText: luisa.swatch('white', '100'),
    },
    secondary: {
        main: luisa.swatch('devblue', '60'),
        dark: luisa.swatch('devblue', '100'),
        light: luisa.swatch('devblue', '10'),
        contrastText: luisa.swatch('white', '100'),
    },
    error: {
        main: luisa.swatch('red', '60'),
        dark: luisa.swatch('red', '100'),
        light: luisa.swatch('red', '10'),
        contrastText: luisa.swatch('white', '100'),
    },
    warning: {
        main: luisa.swatch('yellow', '60'),
        dark: luisa.swatch('yellow', '100'),
        light: luisa.swatch('yellow', '10'),
        contrastText: luisa.swatch('gray', '100'),
    },
    info: {
        main: luisa.swatch('headset', '60'),
        dark: luisa.swatch('headset', '100'),
        light: luisa.swatch('headset', '10'),
        contrastText: luisa.swatch('white', '100'),
    },
    success: {
        main: luisa.swatch('green', '60'),
        dark: luisa.swatch('green', '100'),
        light: luisa.swatch('green', '10'),
        contrastText: luisa.swatch('white', '100'),
    },
    text: {
        primary: luisa.swatch('devblue', '100'),
        secondary: luisa.swatch('gray', '90'),
        disabled: luisa.swatch('gray', '60'),
    },
    background: {
        default: luisa.swatch('background', '10'),
        paper: luisa.swatch('white', '100'),
        dark: luisa.swatch('background', '100'),
        light: luisa.swatch('background', '10'),
    },
    divider: luisa.swatch('gray', '30'),
};

export const typography = {
    fontFamily: 'Roboto, sans-serif',
    h1: {
        fontSize: '34px',
        lineHeight: '40px',
        fontFamily: 'Roboto-medium, Roboto, sans-serif',
        fontWeight: 400,
        letterSpacing: '0.25px',
    },
    h2: {
        fontSize: '24px',
        lineHeight: '28px',
        fontFamily: 'Roboto-medium, Roboto, sans-serif',
        fontWeight: 500,
        letterSpacing: 0,
    },
    h3: {
        fontSize: '20px',
        lineHeight: '24px',
        fontFamily: 'Roboto-medium, Roboto, sans-serif',
        fontWeight: 500,
        letterSpacing: '0.15px',
    },
    h4: {
        fontSize: '18px',
        lineHeight: '22px',
        fontFamily: 'Roboto-medium, Roboto, sans-serif',
        fontWeight: 500,
        letterSpacing: 0,
    },
    h5: {
        fontSize: '16px',
        lineHeight: '24px',
        fontFamily: 'Roboto-medium, Roboto, sans-serif',
        fontWeight: 500,
        letterSpacing: '0.15px',
    },
    h6: {
        fontSize: '14px',
        lineHeight: '21px',
        fontFamily: 'Roboto-medium, Roboto, sans-serif',
        fontWeight: 500,
        letterSpacing: '0.15px',
    },
    body0: {
        fontSize: '18px',
        lineHeight: '27px',
        fontFamily: 'Roboto-regular, Roboto, sans-serif',
    },
    body1: {
        fontSize: '16px',
        lineHeight: '24px',
        fontFamily: 'Roboto-regular, Roboto, sans-serif',
    },
    body2: {
        fontSize: '14px',
        lineHeight: '20px',
        fontFamily: 'Roboto-regular, Roboto, sans-serif',
    },
    subtitle1: {
        fontSize: '14px',
        lineHeight: '20px',
        fontFamily: 'Roboto-regular, Roboto, sans-serif',
        opacity: 0.72,
    },
    caption: {
        fontSize: '12px',
        lineHeight: '18px',
        opacity: 0.72,
    },
    link: {
        fontSize: '14px',
        lineHeight: '20px',
        color: colors.primary.main,
        textDecoration: 'none',
    },
};

/**
 * Convert Luisa's spacing values to MUI's spacing values.
 *
 * @param factor - The spacing factor to convert.
 * @returns The converted spacing value in rem.
 */
export const spacings = (factor: number): string | number => {
    const spacingMap: Record<number, number> = {
        0: 0,
        0.25: luisa.spacings['4xs'], // 0.25rem (4px)
        0.5: luisa.spacings['3xs'], // 0.5rem (8px)
        0.75: luisa.spacings['2xs'], // 0.75rem (12px)
        1: luisa.spacings.xs, // 1rem (16px)
        1.25: luisa.spacings.sm, // 1.25rem (20px)
        1.5: luisa.spacings.md, // 1.5rem (24px)
        2: luisa.spacings.lg, // 2rem (32px)
        2.5: luisa.spacings.xl, // 2.5rem (40px)
        3: luisa.spacings['2xl'], // 3rem (48px)
        3.5: luisa.spacings['3xl'], // 3.5rem (56px)
        4: luisa.spacings['4xl'], // 4rem (64px)
        5: luisa.spacings['5xl'], // 5rem (80px)
    };

    return `${spacingMap[factor] !== undefined ? spacingMap[factor] : factor}rem`;
};

export const shadows = [...luisa.shadows, ...Array(21).fill('none')];
